import type { Maybe } from '@adornis/base/utilTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { EntityData } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { WorkshopAppointment } from './WorkshopAppointment.js';
import { WorkshopAttendance } from './WorkshopAttendance.js';
import { WorkshopTrainingAttendance } from './WorkshopTrainingAttendance.js';

export const WorkshopRoleTypes = ['Übungsleitungen (ÜL)', 'Feste Mitarbeitende (MA)', 'Kooperationspartner:innen (KP)'];

/**
 * @entity Workshop
 */
@Entity()
export class Workshop extends MongoEntity {
  public static override _class = 'Workshop';
  public static override _collectionName = 'workshops';

  @validate(nonOptional())
  @Field(() => String)
  public title: Maybe<string>;

  @validate(nonOptional())
  @Field(() => String)
  public description: Maybe<string>;

  @validate(nonOptional())
  @Field(() => String)
  public appointmentInfo: Maybe<string>;

  @validate(nonOptional())
  @Field(() => String)
  public audience: Maybe<string>;

  @Field(() => Boolean)
  public active: Maybe<boolean>;

  @Field(() => Boolean)
  public showAsOnboarding: Maybe<boolean>;

  @Field(() => Boolean)
  public requiresTraining: Maybe<boolean>;

  @Field(() => [String])
  public roles: Maybe<string[]>;

  @Field(() => String)
  public mediaID: Maybe<string>;

  @DataLoadedField(() => ImageFile, ({ mediaID }) => mediaID)
  public media: Maybe<ImageFile>;

  @Field(() => [WorkshopAppointment], {
    resolve: function (this: EntityData<Workshop>) {
      return async () => {
        const col = await getRawCollection<EntityData<WorkshopAppointment>>(WorkshopAppointment._collectionName);
        return (await col.find({ workshopID: this._id }).toArray()) as WorkshopAppointment[];
      };
    },
  })
  public appointments: Maybe<WorkshopAppointment[]>;

  public isRoleAllowed(role: string): boolean {
    if (!this.roles || this.roles.length === 0) {
      return true;
    }
    return this.roles.includes(role);
  }

  public async isCurrentUserAllowed() {
    const user = currentUser();
    if (!user.optimisticValue.fullyLoaded || !user.optimisticValue.data) {
      return false;
    }

    return user.optimisticValue.data.roles.map(r => r._id).some(role => this.isRoleAllowed(role));
  }

  public static override async afterRemove(id: string, entityData: any) {
    const attendanceCol = await getRawCollection<WorkshopAttendance>(WorkshopAttendance._collectionName);
    await attendanceCol.deleteMany({
      _class: { $in: [WorkshopAttendance._class, WorkshopTrainingAttendance._class] },
      workshopID: id,
    });

    return super.afterRemove(id, entityData);
  }
}
